import { fmtNum } from "@/lib/wallet";
import TokenIcon from "@/components/TokenIcon";
import { ArrowUpRight, Lock, Flame, TrendingUp } from "lucide-react";

export default function TokenCard({ token, accent, accentGrad, accentGlow, onStake, ctaLabel = "Stake →", index = 0 }) {
  const isEvm = accent === "var(--evm)";
  const accentRing = isEvm
    ? "linear-gradient(135deg, #0055ff, #00d4ff)"
    : "linear-gradient(135deg, #9945ff, #14f195)";
  const ctaGrad = isEvm ? "var(--evm-grad)" : "var(--sol-grad)";

  return (
    <button
      onClick={onStake}
      data-testid={`token-card-${token.symbol.toLowerCase()}`}
      className="text-left relative overflow-hidden group fade-up stagger lift"
      style={{
        background: "var(--surface-1)",
        border: "1px solid var(--border)",
        "--i": index,
      }}
    >
      {/* Top accent line (becomes gradient on hover) */}
      <div className="absolute left-0 right-0 top-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: accentRing }} />

      {/* Decorative ambient glow */}
      <div
        className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-0 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none"
        style={{ background: accentRing, filter: "blur(40px)" }}
      />

      {/* Header: icon + name + chain dot */}
      <div className="relative flex items-start justify-between p-5 pb-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            {/* Gradient ring on hover */}
            <div
              className="absolute -inset-[2px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: accentRing }}
            />
            <div className="relative">
              <TokenIcon symbol={token.symbol} color={token.color} size="md" />
            </div>
          </div>
          <div>
            <div className="font-display font-extrabold text-lg leading-none tracking-tighter">{token.symbol}</div>
            <div className="text-[11px] mt-1" style={{ color: "var(--text-3)" }}>{token.name}</div>
          </div>
        </div>
        <ArrowUpRight
          size={16}
          className="opacity-25 group-hover:opacity-100 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          style={{ color: accent }}
        />
      </div>

      {/* Main APR — hero */}
      <div className="relative px-5 pb-4">
        <div className="flex items-end justify-between mb-2">
          <div>
            <div className="text-[9px] uppercase tracking-[0.25em] mb-1" style={{ color: "var(--text-3)" }}>Boosted APR</div>
            <div className="flex items-baseline gap-1">
              <span className={`font-mono font-black text-4xl leading-none tracking-tight tabular-nums ${accentGrad}`}>
                {token.apr.toFixed(2)}
              </span>
              <span className={`font-display font-bold text-lg ${accentGrad}`}>%</span>
            </div>
          </div>
          {/* Decorative mini-sparkline */}
          <MiniSparkline accent={isEvm ? "#00d4ff" : "#14f195"} symbol={token.symbol} />
        </div>
      </div>

      {/* Boost band */}
      <div className="relative mx-5 mb-4 flex items-center justify-between px-3 py-2 overflow-hidden" style={{ background: "rgba(255,69,0,0.06)" }}>
        <div className="absolute left-0 top-0 bottom-0 w-[2px] grad-boost-bg" />
        <div className="flex items-center gap-1.5">
          <Flame size={11} className="grad-boost-text" />
          <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-2)" }}>24h Boost</span>
        </div>
        <span className="font-mono font-bold text-sm grad-boost-text tabular-nums">
          {token.boostApr?.toFixed(0) ?? "—"}%
        </span>
      </div>

      {/* Stats grid */}
      <div className="relative px-5 pb-4 grid grid-cols-2 gap-3">
        <Stat label="TVL" value={`$${fmtNum(token.tvl, 1)}`} />
        <Stat
          label="Lockup"
          value={
            token.lockup === 0 ? (
              <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /> Flexible</span>
            ) : (
              <span className="inline-flex items-center gap-1"><Lock size={9} />{token.lockup}d</span>
            )
          }
        />
      </div>

      {/* CTA */}
      <div
        className={`relative h-11 inline-flex items-center justify-center w-full text-xs font-bold tracking-[0.15em] uppercase transition-all ${accentGlow}`}
        style={{ background: ctaGrad, color: "#fff" }}
      >
        <span className="group-hover:translate-x-0.5 transition-transform">{ctaLabel}</span>
        <ArrowUpRight size={13} className="ml-1.5 group-hover:rotate-45 transition-transform" />
      </div>
    </button>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest mb-1" style={{ color: "var(--text-3)" }}>{label}</div>
      <div className="font-mono font-bold text-sm" style={{ color: "var(--text-1)" }}>{value}</div>
    </div>
  );
}

// Decorative deterministic mini-sparkline (pure svg, no real data — for visual richness)
function MiniSparkline({ accent, symbol }) {
  // Seed from symbol so it's stable per token
  const seed = [...symbol].reduce((a, c) => a + c.charCodeAt(0), 0);
  const rand = (i) => {
    const x = Math.sin(seed * 9999 + i * 47) * 10000;
    return x - Math.floor(x);
  };
  const points = Array.from({ length: 14 }, (_, i) => {
    const x = (i / 13) * 70;
    const y = 18 + (rand(i) - 0.5) * 18;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg width="70" height="32" viewBox="0 0 70 32" className="opacity-60 group-hover:opacity-100 transition-opacity">
      <defs>
        <linearGradient id={`spark-${symbol}`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor={accent} stopOpacity="0.2" />
          <stop offset="100%" stopColor={accent} stopOpacity="1" />
        </linearGradient>
      </defs>
      <polyline
        fill="none"
        stroke={`url(#spark-${symbol})`}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points.join(" ")}
      />
      <circle cx={points[points.length - 1].split(",")[0]} cy={points[points.length - 1].split(",")[1]} r="2" fill={accent} />
    </svg>
  );
}
